import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, TrendingUp, Award, Globe, CheckCircle2 } from 'lucide-react';
import { ROUTE_PATHS, useLanguage } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

const iconMap = {
  Building2,
  TrendingUp,
  Award,
  Globe
};

export default function Home() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      <section className="relative min-h-screen bg-charcoal text-charcoal-foreground overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal to-charcoal/95" />

        <div className="container mx-auto px-4 py-28 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="flex flex-col items-center text-center max-w-4xl mx-auto"
          >
            {/* Company Logo */}
            <motion.img
              src={IMAGES.SHONCHIN_LOGO_TRANSPARENT}
              alt="SHONCHIN MANOR & CO."
              className="h-28 md:h-36 w-auto object-contain mb-10"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              style={{ filter: 'brightness(0) invert(1)' }}
            />

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-5 leading-tight tracking-tight">
              {t.home.hero.title.split('\n').map((line, i, arr) => (
                <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
              ))}
            </h1>
            <p className="text-xl md:text-2xl text-primary mb-6 font-medium tracking-widest uppercase">
              SHONCHIN MANOR &amp; CO.
            </p>
            <p className="text-lg text-charcoal-foreground/75 mb-10 leading-relaxed max-w-2xl">
              {t.home.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl px-8 py-3 font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Link to={ROUTE_PATHS.CONTACT}>
                  {t.home.hero.cta}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white/25 text-white hover:bg-white/10 rounded-xl px-8 py-3 font-semibold"
              >
                <Link to={ROUTE_PATHS.SERVICES}>{t.home.hero.learnMore}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>


      <section className="py-24 bg-cream">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                {t.home.about.title}<br /><span className="text-primary">{t.home.about.titleHighlight}</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t.home.about.description}
              </p>
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
              >
                <Link to={ROUTE_PATHS.ABOUT}>
                  {t.home.about.learnMore}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={springPresets.gentle}
              className="relative"
            >
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl mb-4">
                  <img
                    src={IMAGES.TEAM_MEETING_5}
                    alt="Team Meeting"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={IMAGES.DUBAI_OFFICE_3}
                    alt="Dubai Office"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <div className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t.home.servicesSection.badge}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t.home.servicesSection.title}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { id: 'digitalTwin', title: t.services.digitalTwin.title, description: t.services.digitalTwin.description, img: IMAGES.AARON_ARAB_HANDSHAKE },
              { id: 'verticalStrategy', title: t.services.verticalStrategy.title, description: t.services.verticalStrategy.description, img: IMAGES.CONSULTING_HERO_3 },
              { id: 'crossDomain', title: t.services.crossDomain.title, description: t.services.crossDomain.description, img: IMAGES.CONSULTING_HERO_4 },
            ].map((service) => (
                <motion.div key={service.id} variants={staggerItem}>
                  <div className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group">
                    <div className="h-48 overflow-hidden">
                      <img
                        src={service.img}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-foreground mb-3">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {service.description}
                      </p>
                      <Link
                        to={ROUTE_PATHS.SERVICES}
                        className="text-primary hover:text-primary/80 font-semibold inline-flex items-center group"
                      >
                        {t.home.servicesSection.learnDetail}
                        <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-cream">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t.home.whySection.title}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {[
              {
                icon: Award,
                title: t.home.whySection.item1Title,
                description: t.home.whySection.item1Desc
              },
              {
                icon: Building2,
                title: t.home.whySection.item2Title,
                description: t.home.whySection.item2Desc
              },
              {
                icon: CheckCircle2,
                title: t.home.whySection.item3Title,
                description: t.home.whySection.item3Desc
              }
            ].map((item, index) => (
              <motion.div key={index} variants={staggerItem}>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-charcoal text-charcoal-foreground">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t.home.cta.title}
            </h2>
            <p className="text-lg text-charcoal-foreground/80 mb-8 leading-relaxed">
              {t.home.cta.description}
            </p>
            <Button
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl px-8 py-6 text-lg font-semibold shadow-lg"
            >
              <Link to={ROUTE_PATHS.CONTACT}>
                {t.home.cta.button}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}